import type { TrackingEvent } from '../types/TrackingEvents'
import {
  findEventTypeCalendarGroupElement,
  highlightedCalendarGroupElementIds,
  isHighlightedCalendarGroupRowHidden,
} from './highlightedCalendarGroupElementIds'
import { HighlightScrollDecision, scrollToHighlightedCalendarGroup } from './scrollToHighlightedCalendarGroup'

type HighlightScrollInputs = {
  /** Events of the calendar's loaded range, by game id, after the filters. */
  events: { [gameId: string]: Pick<TrackingEvent, 'eventId' | 'typeId' | 'blueprintEvolutionData'>[] | undefined }
  groupByBlueprint: boolean
  /** Time range the loaded events were requested for, by game id. Missing when the view does not request per game. */
  loadedRangeByGame?: { [gameId: string]: { startTimestamp: number; endTimestamp: number } | undefined }
  /** True once the calendar's time range has stopped changing and no events are being loaded. */
  dataSettled: boolean
}

type DiagnosticsWindow = Window & { __gr3000Decisions?: unknown[] }

/** Lets the read-only console diagnostic of GR-3000 see what the loop decides. Does nothing unless the diagnostic created the list. */
const reportDecisionToDiagnostics = (decision: HighlightScrollDecision) => {
  const decisions = (window as DiagnosticsWindow).__gr3000Decisions
  if (Array.isArray(decisions)) decisions.push({ t: Math.round(performance.now()), ...decision })
}

type CreateHighlightScrollOptionsParams = {
  trackedGameId?: string
  eventType?: string
  highlightedEventId?: string
  /** Start of the highlighted instance. The events count as loaded only once they were requested for a range that contains it. */
  eventStartTime?: number
  headerOffset: number
  /** Read on every frame: the events, grouping and loading state change while the calendar loads the moved-to range. */
  getInputs: () => HighlightScrollInputs
}

/**
 * Options for `scrollToHighlightedCalendarGroup` for the event highlighted through the Highlight button.
 */
export const createHighlightScrollOptions = ({
  trackedGameId,
  eventType,
  highlightedEventId,
  eventStartTime,
  headerOffset,
  getInputs,
}: CreateHighlightScrollOptionsParams): Parameters<typeof scrollToHighlightedCalendarGroup>[0] => {
  // Events that were loaded before the calendar moved to the event's dates say nothing about the event's row.
  const isDataSettled = () => {
    const { dataSettled, loadedRangeByGame } = getInputs()
    const loadedRange = trackedGameId ? loadedRangeByGame?.[trackedGameId] : undefined
    const loadedRangeContainsEvent =
      !loadedRange || eventStartTime === undefined || (loadedRange.startTimestamp <= eventStartTime && eventStartTime <= loadedRange.endTimestamp)

    return dataSettled && loadedRangeContainsEvent
  }

  return {
    getElementIds: () => {
      const { events, groupByBlueprint } = getInputs()

      return highlightedCalendarGroupElementIds({
        trackedGameId,
        eventType,
        highlightedEventId,
        gameEvents: trackedGameId ? events[trackedGameId] : undefined,
        groupByBlueprint,
      })
    },
    getFallbackElement: () => findEventTypeCalendarGroupElement(trackedGameId, eventType),
    isRowHidden: () => {
      const { events, groupByBlueprint } = getInputs()

      return (
        isDataSettled() &&
        isHighlightedCalendarGroupRowHidden({
          highlightedEventId,
          gameEvents: trackedGameId ? events[trackedGameId] : undefined,
          groupByBlueprint,
        })
      )
    },
    isDataSettled,
    headerOffset,
    onDecision: reportDecisionToDiagnostics,
  }
}
